import logger from '../utils/logger';
import { workflowStorageService } from './workflowStorageService';

export interface SaltData {
    salt: string;
    metadata: {
        generated: number;
        algorithm: string;
        length: number;
        description?: string;
        contractAddress?: string;
        functionName?: string;
    };
}

export interface StoredSalt {
    id: string;
    salt: string;
    metadata: SaltData['metadata'];
    created: number;
    lastModified: number;
    lastAccessed: number;
}

class SaltStorageService {
    private baseUrl: string;

    constructor() {
        // Environment-agnostic URL - works with Vite dev proxy and Vercel production
        // No more port coordination issues - Vite dev server handles the routing
        this.baseUrl = '/api/workflow-storage';
    }

    /**
     * Generate a new cryptographically secure salt
     * SECURITY FIX: Delegate to workflowStorageService which has Auth0 integration
     */
    async generateSalt(): Promise<{ salt: string; metadata: any }> {
        try {
            logger.log('🧂 Attempting backend salt generation...');
            const result = await workflowStorageService.generateSalt();

            // Cast to any for checking both response formats
            const resultAny = result as any;

            logger.log('🔍 Backend salt generation response:', {
                success: result.success,
                hasData: !!result.data,
                hasSalt: !!result.data?.salt,
                hasTopLevelSalt: !!resultAny.salt,
                error: result.error
            });

            // CRITICAL FIX: Backend returns salt at top level, not nested in data
            // Handle both formats: {data: {salt, metadata}} and {salt, metadata}
            if (!result.success) {
                const errorMsg = result.error || 'Failed to generate salt';
                logger.error('❌ Backend salt generation failed:', errorMsg);
                logger.error('Full response:', result);
                throw new Error(errorMsg);
            }

            // Check for salt in data object first (expected format)
            if (result.data?.salt) {
                logger.log('✅ Salt generated successfully from backend (nested format):', result.data.salt);
                return result.data;
            }

            // FALLBACK: Check for salt at top level (actual backend format)
            if (resultAny.salt) {
                logger.log('✅ Salt generated successfully from backend (top-level format):', resultAny.salt);
                return {
                    salt: resultAny.salt,
                    metadata: resultAny.metadata || {}
                };
            }

            // No salt found in either location
            logger.error('❌ Backend returned success but no salt found');
            logger.error('Full response:', result);
            throw new Error('Backend returned success but no salt data');
        } catch (error) {
            // Improved error logging - extract actual error message
            logger.error('❌ Salt generation error caught:');
            logger.error('Error type:', error instanceof Error ? error.constructor.name : typeof error);
            logger.error('Error message:', error instanceof Error ? error.message : String(error));
            logger.error('Error stack:', error instanceof Error ? error.stack?.split('\n').slice(0, 5).join('\n') : 'No stack');

            const errorMessage = error instanceof Error ? error.message : String(error);
            throw new Error(`Salt generation failed: ${errorMessage}`);
        }
    }

    /**
     * Store a salt value with metadata
     * SECURITY FIX: Delegate to workflowStorageService which has Auth0 integration
     */
    async storeSalt(saltData: SaltData, description?: string): Promise<{ success: boolean; id: string }> {
        try {
            const enhancedMetadata = {
                ...saltData.metadata,
                description: description || `Salt generated at ${new Date().toISOString()}`,
                stored: Date.now()
            };

            const result = await workflowStorageService.storeSalt({
                salt: saltData.salt,
                metadata: enhancedMetadata
            });

            if (!result.success) {
                throw new Error(result.error || 'Failed to store salt');
            }

            logger.log('Salt stored successfully with ID:', result.id);
            return {
                success: true,
                id: result.id!
            };
        } catch (error) {
            logger.error('Error storing salt:', error);
            throw error;
        }
    }

    /**
     * Retrieve a stored salt by ID
     * SECURITY FIX: Delegate to workflowStorageService which has Auth0 integration
     */
    async retrieveSalt(id: string): Promise<SaltData> {
        try {
            const result = await workflowStorageService.retrieveSalt(id);

            if (!result.success || !result.data) {
                throw new Error(result.error || 'Failed to retrieve salt');
            }

            logger.log('Salt retrieved successfully:', id);
            // Convert from workflowStorageService format to saltStorageService format
            return {
                salt: result.data.salt,
                metadata: {
                    generated: result.data.metadata?.generated || Date.now(),
                    algorithm: result.data.metadata?.algorithm || 'crypto.randomBytes',
                    length: result.data.metadata?.length || 32,
                    description: result.data.metadata?.purpose,
                    contractAddress: undefined,
                    functionName: undefined
                }
            };
        } catch (error) {
            logger.error('Error retrieving salt:', error);
            throw error;
        }
    }

    /**
     * List all stored salts (metadata only)
     * SECURITY FIX: Delegate to workflowStorageService which has Auth0 integration
     * EMPTY STORE FIX: Return empty array instead of error for fresh deployments
     */
    async listSalts(): Promise<StoredSalt[]> {
        try {
            const result = await workflowStorageService.listSalts();

            if (!result.success) {
                // CRITICAL FIX: Distinguish between empty store and actual errors
                const errorMsg = result.error || '';

                // Empty store scenarios (not errors, just no data yet)
                if (errorMsg.includes('No data found') ||
                    errorMsg.includes('empty') ||
                    errorMsg.includes('not found') ||
                    errorMsg.includes('FUNCTION_INVOCATION_FAILED')) {
                    logger.log('ℹ️ KV store is empty (normal for fresh deployment) - returning empty array');
                    return [];
                }

                // Actual error - throw it
                logger.error('❌ Actual error listing salts:', errorMsg);
                throw new Error(errorMsg || 'Failed to list salts');
            }

            logger.log('✅ Salts listed successfully:', result.data?.length || 0);

            // Handle empty data array
            if (!result.data || result.data.length === 0) {
                logger.log('ℹ️ No salts found in KV store (normal for fresh deployment)');
                return [];
            }

            // Convert from workflowStorageService format to saltStorageService format
            return result.data.map(item => ({
                id: item.id,
                salt: '', // Not included in list response
                metadata: {
                    generated: item.timestamp,
                    algorithm: 'crypto.randomBytes',
                    length: 32
                },
                created: item.timestamp,
                lastModified: item.timestamp,
                lastAccessed: item.timestamp
            }));
        } catch (error) {
            // CRITICAL FIX: Catch network/parsing errors and return empty array
            const errorMsg = error instanceof Error ? error.message : String(error);

            // If it's a non-JSON response (500 error) or connection issue, treat as empty store
            if (errorMsg.includes('non-JSON response') ||
                errorMsg.includes('FUNCTION_INVOCATION_FAILED') ||
                errorMsg.includes('NetworkError') ||
                errorMsg.includes('Failed to fetch')) {
                logger.log('⚠️ KV store unavailable or empty (returning empty array):', errorMsg);
                return [];
            }

            // Real error - log and throw
            logger.error('❌ Unexpected error listing salts:', error);
            throw error;
        }
    }

    /**
     * Delete a stored salt
     * SECURITY FIX: Delegate to workflowStorageService which has Auth0 integration
     */
    async deleteSalt(id: string): Promise<{ success: boolean }> {
        try {
            const result = await workflowStorageService.deleteSalt(id);

            if (!result.success) {
                throw new Error(result.error || 'Failed to delete salt');
            }

            logger.log('Salt deleted successfully:', id);
            return { success: true };
        } catch (error) {
            logger.error('Error deleting salt:', error);
            throw error;
        }
    }

    /**
     * Generate and store a salt in one operation
     */
    async generateAndStoreSalt(description?: string, contractAddress?: string, functionName?: string): Promise<{ salt: string; id: string }> {
        try {
            // Generate the salt
            const { salt, metadata } = await this.generateSalt();

            // Enhance metadata with context
            const enhancedSaltData: SaltData = {
                salt,
                metadata: {
                    ...metadata,
                    description: description || `Salt for ${contractAddress ? contractAddress.substring(0, 10) + '...' : 'transaction'}`,
                    contractAddress,
                    functionName
                }
            };

            // Store the salt
            const { id } = await this.storeSalt(enhancedSaltData, description);

            return { salt, id };
        } catch (error) {
            logger.error('Error generating and storing salt:', error);
            throw error;
        }
    }

    /**
     * Get a salt for reuse (retrieve existing or generate new)
     */
    async getSaltForContext(contractAddress?: string, functionName?: string): Promise<{ salt: string; id: string; isNew: boolean }> {
        try {
            // Try to find an existing salt for this context
            const existingSalts = await this.listSalts();

            const contextMatch = existingSalts.find(stored => {
                // Try to retrieve the full salt data to check metadata
                // For now, we'll generate a new salt each time
                // In a more sophisticated implementation, you could cache or match by context
                return false;
            });

            if (contextMatch) {
                const saltData = await this.retrieveSalt(contextMatch.id);
                return {
                    salt: saltData.salt,
                    id: contextMatch.id,
                    isNew: false
                };
            }

            // Generate a new salt
            const { salt, id } = await this.generateAndStoreSalt(
                `Salt for ${functionName || 'function'} on ${contractAddress?.substring(0, 10) || 'contract'}...`,
                contractAddress,
                functionName
            );

            return { salt, id, isNew: true };
        } catch (error) {
            logger.error('Error getting salt for context:', error);
            throw error;
        }
    }
}

// Export a singleton instance
const saltStorageService = new SaltStorageService();
export default saltStorageService;
